import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { TranslateModule } from '@ngx-translate/core';
import {
  NotificationBackendService,
  BackendNotification,
  NotificationStatus,
  NotificationQuery,
  NotificationTabCounts
} from '../../services/notification-backend.service';
import { AuthService } from '../../services/auth.service';
import { UserPermissions, userHasPermission } from '../../services/permissions';
import { NotifyBroadcastPanelComponent } from '../notify-broadcast/notify-broadcast-panel.component';

type TypeFilter = 'all' | NotificationStatus;
type ReadFilter = 'all' | 'unread' | 'read';
type Tab = 'personal' | 'admin' | 'entity';

/**
 * Pagina dedicata allo storico completo delle notifiche, divisa in tre tab
 * (Personali / Admin / Entity). Filtri (stato lettura, tipo) + ricerca + paginazione
 * sono TUTTI lato server: ogni cambio di contesto rifetcha la pagina 0, l'infinite
 * scroll richiede l'offset successivo. Ricarica anche al cambio di entity.
 */
@Component({
  selector: 'app-notifications-page',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NotifyBroadcastPanelComponent],
  templateUrl: './notifications-page.component.html',
  styleUrl: './notifications-page.component.scss'
})
export class NotificationsPageComponent implements OnInit, AfterViewInit, OnDestroy {
  items: BackendNotification[] = [];
  counts: NotificationTabCounts = { personal: 0, admin: 0, entity: 0 };

  loading = false;       // caricamento pagina 0
  loadingMore = false;   // caricamento pagina successiva

  search = '';
  activeTab: Tab = 'personal';
  activeType: TypeFilter = 'all';
  readFilter: ReadFilter = 'all';

  readonly typeFilters: readonly TypeFilter[] = ['all', 'info', 'success', 'warning', 'error'];
  readonly readFilters: readonly ReadFilter[] = ['all', 'unread', 'read'];

  private readonly LIMIT = 20;
  private offset = 0;
  hasMore = false;

  @ViewChild('loadMoreSentinel') sentinel?: ElementRef<HTMLElement>;
  private observer?: IntersectionObserver;
  private readonly search$ = new Subject<string>();
  private readonly subs = new Subscription();

  notifyBroadcastPanelOpen = false;

  // Selezione multipla per il "segna come lette" in blocco. Contiene solo ID di
  // notifiche NON lette: una riga già letta non ha nulla da applicare.
  readonly selectedIds = new Set<string>();

  constructor(
    private notificationBackend: NotificationBackendService,
    private router: Router,
    private auth: AuthService,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    // selectedDcr$ è un BehaviorSubject: la prima emissione fa il caricamento
    // iniziale; ogni cambio entity (incluso il passaggio a "Admin" = null) ricarica.
    this.subs.add(
      this.auth.selectedDcr$.pipe(distinctUntilChanged()).subscribe(() => {
        if (this.activeTab === 'admin' && !this.showAdminTab) this.activeTab = 'personal';
        this.reset();
      })
    );
    // Ricerca con debounce per non lanciare una richiesta a ogni tasto.
    this.subs.add(
      this.search$.pipe(debounceTime(300), distinctUntilChanged()).subscribe(() => this.reset())
    );
  }

  ngAfterViewInit(): void {
    // root null = viewport: funziona anche se a scrollare è un container annidato.
    this.observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) this.zone.run(() => this.loadMore());
      },
      { rootMargin: '150px' }
    );
    if (this.sentinel) this.observer.observe(this.sentinel.nativeElement);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.observer?.disconnect();
  }

  // La query backend restituisce tutto solo per admin SENZA entity selezionata
  // (`hasGlobalDataAccess && !entityID`); in ogni altro caso la vista è ristretta.
  get isScopedView(): boolean {
    return !this.auth.hasGlobalDataScope();
  }

  // Il tab "Admin" è mostrato solo in vista piena (select principale su "Admin").
  get showAdminTab(): boolean {
    return !this.isScopedView;
  }

  // Stesso gate del pannello di invio e del "+" nel centro notifiche.
  get canBroadcastNotifications(): boolean {
    return userHasPermission(
      this.auth.currentUserValue?.permissions,
      UserPermissions.SPECIAL_FEATURES_CAN_BROADCAST
    );
  }

  openNotifyBroadcastPanel(): void {
    this.notifyBroadcastPanelOpen = true;
  }

  // Il pannello emette `close` sia dopo l'invio sia in annullamento, senza
  // distinguere: ricarichiamo sempre, così la notifica appena creata (l'admin è
  // fra i destinatari quando il target è "admin") compare subito in lista.
  closeNotifyBroadcastPanel(): void {
    this.notifyBroadcastPanelOpen = false;
    this.reset();
  }

  private buildQuery(offset: number): NotificationQuery {
    return {
      status: this.readFilter,
      target: this.activeTab,
      messageType: this.activeType === 'all' ? undefined : this.activeType,
      q: this.search.trim() || undefined,
      limit: this.LIMIT,
      offset
    };
  }

  // Ricarica da capo (pagina 0) + conteggi tab: chiamata a ogni cambio di contesto.
  // La selezione si azzera: gli ID selezionati non sopravvivono a un cambio di
  // filtro/tab, dove le righe corrispondenti potrebbero non essere più in lista.
  private reset(): void {
    this.offset = 0;
    this.items = [];
    this.hasMore = false;
    this.selectedIds.clear();
    this.loadPage(true);
    this.loadCounts();
  }

  private loadPage(initial: boolean): void {
    if (initial) this.loading = true; else this.loadingMore = true;
    const requestedOffset = this.offset;
    this.notificationBackend.fetchNotifications(this.buildQuery(requestedOffset)).subscribe({
      next: rows => {
        this.items = requestedOffset === 0 ? rows : [...this.items, ...rows];
        this.hasMore = rows.length === this.LIMIT;
        this.offset = requestedOffset + rows.length;
        this.loading = false;
        this.loadingMore = false;
      },
      error: () => { this.loading = false; this.loadingMore = false; }
    });
  }

  private loadCounts(): void {
    this.notificationBackend.fetchNotificationCounts({
      status: this.readFilter,
      messageType: this.activeType === 'all' ? undefined : this.activeType,
      q: this.search.trim() || undefined
    }).subscribe(c => this.counts = c);
  }

  loadMore(): void {
    if (this.loading || this.loadingMore || !this.hasMore) return;
    this.loadPage(false);
  }

  selectTab(tab: Tab): void {
    if (tab === this.activeTab) return;
    this.activeTab = tab;
    this.reset();
  }

  selectType(type: TypeFilter): void {
    this.activeType = type;
    this.reset();
  }

  selectReadFilter(filter: ReadFilter): void {
    this.readFilter = filter;
    this.reset();
  }

  onSearchChange(): void {
    this.search$.next(this.search);
  }

  get hasAnyNotifications(): boolean {
    return this.counts.personal + this.counts.admin + this.counts.entity > 0;
  }

  typeClass(type: string): string {
    return `notification-item--${type}`;
  }

  // ---- Selezione multipla -------------------------------------------------

  get selectedCount(): number {
    return this.selectedIds.size;
  }

  isSelected(notification: BackendNotification): boolean {
    return this.selectedIds.has(notification.ID);
  }

  toggleSelection(notification: BackendNotification, event: Event): void {
    event.stopPropagation();
    if (this.selectedIds.has(notification.ID)) {
      this.selectedIds.delete(notification.ID);
    } else {
      this.selectedIds.add(notification.ID);
    }
  }

  get allSelected(): boolean {
    return this.items.length > 0 && this.items.every(n => this.selectedIds.has(n.ID));
  }

  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedIds.clear();
    } else {
      this.items.forEach(n => this.selectedIds.add(n.ID));
    }
  }

  clearSelection(): void {
    this.selectedIds.clear();
  }

  // Quante righe selezionate hanno qualcosa da applicare, per stato: guidano
  // quale dei due bottoni in blocco mostrare.
  private selectedWithState(read: boolean): string[] {
    return this.items
      .filter(n => this.selectedIds.has(n.ID) && !!n.Managed !== read)
      .map(n => n.ID);
  }

  get selectedUnreadCount(): number {
    return this.selectedWithState(true).length;
  }

  get selectedReadCount(): number {
    return this.selectedWithState(false).length;
  }

  get unreadInView(): number {
    return this.items.filter(n => !n.Managed).length;
  }

  // ---- Stato di lettura ---------------------------------------------------

  toggleReadState(notification: BackendNotification, event: Event): void {
    event.stopPropagation();
    this.setReadState([notification.ID], !notification.Managed);
  }

  markSelectedAsRead(): void {
    this.setReadState(this.selectedWithState(true), true);
  }

  markSelectedAsUnread(): void {
    this.setReadState(this.selectedWithState(false), false);
  }

  markAllAsRead(): void {
    this.setReadState(this.items.filter(n => !n.Managed).map(n => n.ID), true);
  }

  /**
   * Unica strada per cambiare lo stato di lettura: una sola richiesta con
   * l'array di ID. Lo stato locale si allinea solo sugli ID confermati dal
   * backend, così una riga non aggiornata davvero non cambia aspetto.
   */
  private setReadState(ids: string[], read: boolean): void {
    const pending = ids.filter(id => this.items.some(n => n.ID === id && !!n.Managed !== read));
    if (pending.length === 0) return;

    this.notificationBackend.setReadStateAndUpdate(pending, read).subscribe({
      next: changed => {
        const done = new Set(changed);
        this.items.forEach(n => { if (done.has(n.ID)) n.Managed = read; });
        done.forEach(id => this.selectedIds.delete(id));
        this.loadCounts();
      },
      error: () => { /* già loggato dal service: la lista resta com'era */ }
    });
  }

  // Stessa logica del centro notifiche: segna letta e naviga al referral se presente.
  open(notification: BackendNotification): void {
    if (!notification.Managed) {
      this.setReadState([notification.ID], true);
    }
    if (notification.Refferal) {
      if (notification.Refferal === 'audiences' || notification.Refferal.startsWith('audiences/')) {
        this.router.navigate(['/reach/audiences']);
      } else {
        this.router.navigate([`/${notification.Refferal}`]);
      }
    }
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    const diffMs = new Date().getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  formatFullDate(dateString: string): string {
    return new Date(dateString).toLocaleString();
  }

  trackById(_index: number, notification: BackendNotification): string {
    return notification.ID;
  }
}
